import type { CreateTaskCommentDto, TaskCommentDto } from '@schwrz/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '../shared/api/client';

export const useCommentsStore = defineStore('comments', () => {
  /** Cache keyed by taskId. */
  const byTask = ref(new Map<string, TaskCommentDto[]>());
  const loadingTasks = ref(new Set<string>());

  function listOf(taskId: string): TaskCommentDto[] {
    return byTask.value.get(taskId) ?? [];
  }

  function setFor(taskId: string, comments: TaskCommentDto[]): void {
    const next = new Map(byTask.value);
    next.set(taskId, comments);
    byTask.value = next;
  }

  async function loadTask(taskId: string, force = false): Promise<void> {
    if (loadingTasks.value.has(taskId)) return;
    if (byTask.value.has(taskId) && !force) return;
    loadingTasks.value.add(taskId);
    try {
      const res = await api<{ comments: TaskCommentDto[] }>(
        `/api/tasks/${encodeURIComponent(taskId)}/comments`,
      );
      setFor(taskId, res.comments);
    } finally {
      loadingTasks.value.delete(taskId);
    }
  }

  async function create(payload: CreateTaskCommentDto): Promise<TaskCommentDto> {
    const created = await api<TaskCommentDto>(
      `/api/tasks/${encodeURIComponent(payload.taskId)}/comments`,
      { method: 'POST', body: { text: payload.text } },
    );
    const current = listOf(payload.taskId);
    setFor(payload.taskId, [...current, created]);
    return created;
  }

  async function remove(comment: TaskCommentDto): Promise<void> {
    await api<void>(
      `/api/tasks/${encodeURIComponent(comment.taskId)}/comments/${encodeURIComponent(comment.id)}`,
      { method: 'DELETE' },
    );
    const current = listOf(comment.taskId);
    setFor(
      comment.taskId,
      current.filter((c) => c.id !== comment.id),
    );
  }

  return { byTask, loadingTasks, listOf, loadTask, create, remove };
});
